import { FunctionComponent, useState } from 'react'
import { Chip, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { DeleteOutline, EditOutlined, HighlightOff, SaveOutlined } from '@mui/icons-material';

export const ImportMappingSupplierFormField: FunctionComponent<{
  id?: string,
  customHeading: string,
  mappedHeading: string,
  options: any,
  editField?: string | undefined,
  editHandler: (importMappingId: string) => void,
  deleteHandler: (importMappingId: string) => void,
  submitEditHandler?: (importMappingId: string, customHeading: string, mappedHeading: string) => void,
  submitCreateHandler?: (customHeading: string, mappedHeading: string) => void,
  closeHandler: () => void,
  errorCustom: { id: string, error: string } | undefined,
  errorMapped: { id: string, error: string } | undefined,
  mappedSelections: Map<string, string>,
  setMappedSelections: React.Dispatch<React.SetStateAction<Map<string, string>>>,
  setAddNewHeading?: React.Dispatch<React.SetStateAction<any>>,
  addNewHeading: boolean,
}> = ({
  id,
  customHeading,
  mappedHeading,
  options,
  editField,
  editHandler,
  deleteHandler,
  submitEditHandler,
  submitCreateHandler,
  closeHandler,
  errorCustom,
  errorMapped,
  mappedSelections,
  setMappedSelections,
  setAddNewHeading,
  addNewHeading,
}) => {

    const [localCustomHeading, setLocalCustomHeading] = useState(customHeading)
    const [localMappedHeading, setLocalMappedHeading] = useState(mappedHeading)

    const handleMappedChange = (event: SelectChangeEvent<string>) => {
      const newMappedHeading = event.target.value;
      if (id) {
        setMappedSelections(prev => {
          const updated = new Map(prev);
          updated.set(id, newMappedHeading);
          return updated;
        });
      }
      setLocalMappedHeading(newMappedHeading);
    };

    const saveHandler = () => {
      if (id && submitEditHandler) {
        submitEditHandler(id, localCustomHeading, localMappedHeading)
      }
      if (!id && submitCreateHandler) {
        submitCreateHandler(localCustomHeading, localMappedHeading)
      }
    }

    const cancelHandler = () => {
      setLocalCustomHeading(customHeading)
      setLocalMappedHeading(mappedHeading)
      closeHandler()
      if (setAddNewHeading) {
        setAddNewHeading(false)
      }
    }

    return (
      <Stack sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel htmlFor={`custom-heading-${id}`}>Custom Column Heading</InputLabel>
          <OutlinedInput inputProps={{ min: 0.01, step: 0.01 }}
            id={`custom-heading-${id}`}
            name={`custom-heading-${id}`}
            label='Custom Column Heading'
            value={localCustomHeading}
            disabled={((!editField && !!id) || (!!editField && editField !== id))}
            onChange={(e) => setLocalCustomHeading(e.target.value)}
            type='text'
            error={!!errorCustom && errorCustom.id === id}
            sx={{ color: 'text.primary' }}></OutlinedInput>
          {errorCustom && errorCustom.id === id &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorCustom.error}</FormHelperText>}
        </FormControl>
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel htmlFor={`mapped-heading-${id}`}> Mapped System Field</InputLabel>
          <Select
            id={`mapped-heading-${id}`}
            label="Mapped System Field"
            value={localMappedHeading}
            disabled={((!editField && !!id) || (!!editField && editField !== id))}
            onChange={handleMappedChange}
            renderValue={(selected) => {
              const selectedOption = options.find((option: any) => option.name === selected);
              return <Typography>{selectedOption?.name}</Typography>;
            }}
            error={!!errorMapped && errorMapped.id === id}>
            {options?.filter((o: any) => o.status === 'mandatory').map((option: any, index: number) => {
              const isSelectedByAnother = Object.entries(mappedSelections.entries()).some(
                ([selectedId, selectedValue]) =>
                  selectedValue === option.name && selectedId !== id
              )
              return (
                <MenuItem key={index} value={option.name} disabled={isSelectedByAnother}>
                  <Stack sx={{
                    display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    <Typography>{option.name}</Typography>
                    <Chip color='error' size='small' variant='outlined' sx={{ color: 'error', fontSize: '0.7rem', }} label='Required' />
                  </Stack>
                </MenuItem>
              );
            })}
            {options?.filter((o: any) => o.status === 'optional').map((option: any, index: number) => {
              const isSelectedByAnother = Array.from(mappedSelections.entries()).some(
                ([selectedId, selectedValue]) =>
                  selectedValue === option.name && selectedId !== id)
              return (
                <MenuItem key={index} value={option.name} disabled={isSelectedByAnother}>
                  {option.name}
                </MenuItem>
              );
            })}
          </Select>
          {errorMapped && errorMapped.id === id &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMapped.error}</FormHelperText>}
          {errorCustom && errorCustom.id === id &&
            <FormHelperText sx={{ color: 'white', marginLeft: "16px !important" }}>{errorCustom.error}</FormHelperText>}
        </FormControl>
        {!id &&
          <>
            <IconButton sx={{ mt: 2 }} onClick={cancelHandler}>
              <HighlightOff />
            </IconButton>
            <IconButton sx={{ mt: 2 }} onClick={saveHandler}>
              <SaveOutlined />
            </IconButton>
          </>
        }
        {id && editField !== id &&
          <>
            <IconButton sx={{ mt: 2 }} disabled={(!!editField && editField !== id) || addNewHeading} onClick={() => editHandler(id)}>
              <EditOutlined />
            </IconButton>
            <IconButton sx={{ mt: 2 }} disabled={(!!editField && editField !== id) || addNewHeading} onClick={() => deleteHandler(id)}>
              <DeleteOutline />
            </IconButton>
          </>}
        {id && editField === id &&
          <>
            <IconButton sx={{ mt: 2 }} onClick={cancelHandler}>
              <HighlightOff />
            </IconButton>
            <IconButton sx={{ mt: 2 }} onClick={saveHandler}>
              <SaveOutlined />
            </IconButton>
          </>}
      </Stack>
    )
  }